import { useContext as useContextRPU } from "react";
import { TranscriptInSheet, TranscriptOpener, useTranscriptSheet } from "../lib/transcriptSheet";
import { whenScrollIdle } from "../lib/scrollIdle";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSession } from "../store/session";
import { requestDelegateArchives } from "../lib/delegateArchiveIndex";
import { requestClaudeArchives } from "../lib/claudeArchiveIndex";
import { claudeUpdateName, claudeUpdatePhase, currentClaudeArchive } from "../lib/claudeTimeline";
import { currentDelegateActivity, nativeExecutionForMessage, publicUpdateHistory } from "../lib/delegateActivity";
import { validArchive } from "../lib/delegateTimeline";
import { useDelegateTimeline } from "../lib/useDelegateTimeline";
import { groupRequestPublicUpdates, publicSourceKey, type PublicTimelineSource, type PublicTimelineSegment } from "../lib/requestPublicTimeline";
import type { ClaudeArchive, DelegateArchive, DelegateTimelineEntry, UIMessage } from "../lib/types";
import { DelegateTimelineSegment } from "./DelegateUpdateList";
import { carryOpenCard } from "../lib/openedCards";
import RequestProgressFooter from "./RequestProgressFooter";
import ClaudeRunSteps from "./ClaudeRunSteps";

// Julian (27 Sep): the per-turn "Show steps (N)" drawer stays off in the chat. The steps are
// still recorded on the server for diagnosis; the Opus Updates card is what he reads.
const SHOW_RUN_STEPS = false;
import { requestedProgressName } from "../lib/delegatePresentation";

type Loaded = {key:string;entries:DelegateTimelineEntry[];loading:boolean;error:boolean;hasOlder:boolean;
  loadOlder:()=>void;retry:()=>void};
const EMPTY:DelegateTimelineEntry[] = [];
function SourceLoader({source,enabled,onChange}:{source:PublicTimelineSource;enabled:boolean;onChange:(value:Loaded)=>void}) {
  const timeline = useDelegateTimeline(source.scope,source.entries,enabled ? source.archive : undefined);
  const key = publicSourceKey(source.scope);
  const signature = JSON.stringify([timeline.entries,timeline.loading,timeline.error,timeline.hasOlder]);
  useEffect(() => {
    onChange({key,entries:timeline.entries,loading:timeline.loading,error:timeline.error,
      hasOlder:timeline.hasOlder,loadOlder:timeline.loadOlder,retry:timeline.retry});
  // Hook callbacks use their current cursors; entries/status changes publish a new snapshot.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[key,signature,onChange]);
  return null;
}

/** One request owns its public events, including provider switches and paused runs. */
export default function RequestPublicUpdates({requestMessageId,messages,pending=false}:{requestMessageId:string;messages:UIMessage[];pending?:boolean}) {
  const companyId = useSession(s=>s.activeCompanyId);
  const status = useSession(s=>companyId ? s.sessionStatusByCompany[companyId] : undefined);
  const allMessages = useSession(s=>companyId ? s.messagesByCompany[companyId] : undefined);
  const queue = useSession(s=>companyId ? s.queueByCompany[companyId] : undefined);
  const streamingId = useSession(s=>s.streamingMessageId);
  const ownerKey = (companyId ?? "") + ":" + requestMessageId;
  const marker = useRef<HTMLDivElement>(null);
  const anchor = useRef<{element:Element;top:number;scroller:Element;ownerKey:string}|null>(null);
  const [visible,setVisible] = useState(()=>typeof IntersectionObserver === "undefined");
  const [reload,setReload] = useState(0);
  const [discovered,setDiscovered] = useState<{key:string;sources:PublicTimelineSource[];error:boolean}|null>(null);
  const [retained,setRetained] = useState<{key:string;signature:string;sources:PublicTimelineSource[]}|null>(null);
  const [loaded,setLoaded] = useState<Record<string,Loaded>>({});
  // first: the segment that took over the empty first frame. It keeps that frame's DOM
  // (and Julian's open/closed choice) wherever it later sits, e.g. once an earlier
  // segment of the other provider is loaded in front of it.
  const [groupState,setGroupState] = useState<{key:string;signature:string;groups:PublicTimelineSegment[];first?:string}>({key:ownerKey,signature:"",groups:[]});
  const [engaged,setEngaged] = useState(false);
  // Only this request's candidate does the delivered-turn/full-chat lookup.
  const currentAstra = status?.delegate?.requestMessageId === requestMessageId
    ? currentDelegateActivity(status,companyId,allMessages ?? [],queue ?? [],streamingId)?.delegate : undefined;
  const astra = currentAstra?.requestMessageId === requestMessageId ? currentAstra : undefined;
  const claude = currentClaudeArchive(status,companyId,requestMessageId,
    (queue ?? []).filter(item=>!item.dispatching).map(item=>item.userMsgId ?? item.id));
  if (!engaged && (pending || astra || claude)) setEngaged(true);
  const source = (archive:DelegateArchive|undefined,provider:"astra"|"claude",runId:string,
    entries:DelegateTimelineEntry[]=EMPTY):PublicTimelineSource => ({
      scope:{companyId:companyId!,requestMessageId,runId,provider},archive,entries,
      displayName:provider === "claude" ? claudeUpdateName(archive as ClaudeArchive) : "Astra"});
  const current:PublicTimelineSource[] = [];
  if (astra) current.push({...source(validArchive(astra.archive),"astra",astra.runId,publicUpdateHistory(astra.progress)),
    active:true,phase:astra.status === "waiting" ? "Queued" : astra.status === "verifying" ? "Checking" : "Working"});
  // An open relay observer is not concurrent Claude execution while Astra owns the turn.
  if (claude) current.push({...source(claude,"claude",claude.runId),active:!astra,
    phase:astra ? undefined : claudeUpdatePhase(claude,true)});
  const currentSignature = JSON.stringify(current);
  // Persist only genuine current locators through a Stop/status handover.
  if (current.length && (retained?.key !== ownerKey || retained.signature !== currentSignature)) {
    const values = new Map((retained?.key === ownerKey ? retained.sources : []).map(item=>[publicSourceKey(item.scope),item]));
    for (const item of current) values.set(publicSourceKey(item.scope),{...item,active:false,phase:undefined});
    setRetained({key:ownerKey,signature:currentSignature,sources:[...values.values()]});
  }
  useEffect(()=>{
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(changes=>setVisible(changes.some(entry=>entry.isIntersecting)),
      {rootMargin:"1500px"});
    if(marker.current) observer.observe(marker.current);
    return ()=>observer.disconnect();
  },[]);
  useEffect(()=>{
    // Discover every card when the chat opens, not when it scrolls into view: the lookup
    // is batched (one request per 50 turns), and the collapsed row then exists before the
    // reader gets there, so nothing appears above text being read (29-30 Sep). Details
    // inside a card still load lazily when it is near the screen (SourceLoader).
    if(!companyId) return;
    let cancelled=false;
    void Promise.allSettled([requestDelegateArchives(companyId,requestMessageId),requestClaudeArchives(companyId,requestMessageId)]).then(results=>{
      if(cancelled) return;
      const sources:PublicTimelineSource[]=[];
      if(results[0].status === "fulfilled") for(const run of results[0].value) sources.push({...source(run,"astra",run.runId),
        terminalStatus:run.status === "cancelled" ? "Stopped" : run.status === "failed" ? "Not completed" : "Updates saved"});
      if(results[1].status === "fulfilled") for(const run of results[1].value) sources.push({...source(run,"claude",run.runId),
        terminalStatus:run.state === "stopped" ? "Stopped" : "Updates saved"});
      // Never grow a card under a moving finger (lib/scrollIdle.ts).
      whenScrollIdle(()=>setDiscovered({key:ownerKey,sources,error:results.some(result=>result.status === "rejected")}));
    });
    return ()=>{cancelled=true;};
  // Index discovery is request/visibility/lifecycle based, never one fetch per update.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[companyId,requestMessageId,status?.busy,reload]);
  const byRun = new Map<string,PublicTimelineSource>();
  for (const item of retained?.key === ownerKey ? retained.sources : []) byRun.set(publicSourceKey(item.scope),item);
  for (const item of discovered?.key === ownerKey ? discovered.sources : []) byRun.set(publicSourceKey(item.scope),item);
  for (const message of messages) {
    const execution = nativeExecutionForMessage(message), archive = validArchive(message.meta?.delegateArchive);
    const run = archive?.requestMessageId === requestMessageId ? archive : execution?.requestMessageId === requestMessageId ? execution : undefined;
    if(!run) continue;
    const item = source(archive?.requestMessageId === requestMessageId ? archive : undefined,"astra",run.runId,
      execution?.runId === run.runId ? publicUpdateHistory(execution) : EMPTY);
    const old = byRun.get(publicSourceKey(item.scope));
    byRun.set(publicSourceKey(item.scope),{...item,archive:item.archive ?? old?.archive,
      terminalStatus:execution?.status === "cancelled" ? "Stopped" : execution?.status === "failed" ? "Not completed" : old?.terminalStatus});
  }
  for (const item of current) byRun.set(publicSourceKey(item.scope),item);
  const sources = [...byRun.values()];
  const publish = useCallback((value:Loaded)=>whenScrollIdle(()=>setLoaded(old=>({...old,[value.key]:value}))),[]);
  const hydrated = sources.map(item=>({...item,entries:loaded[publicSourceKey(item.scope)]?.entries ?? item.entries}));
  const signature = JSON.stringify(hydrated);
  let groups = groupState.groups, first = groupState.first;
  if (groupState.key !== ownerKey || groupState.signature !== signature) {
    groups=groupRequestPublicUpdates({companyId:companyId ?? "",requestMessageId},hydrated,
      groupState.key === ownerKey ? groupState.groups : []);
    first = groupState.key === ownerKey ? groupState.first : undefined;
    if (!first && groups[0]) { first = groups[0].key; carryOpenCard(ownerKey+":first",first); }
    setGroupState({key:ownerKey,signature,groups,first});
  }
  const pages = sources.map(item=>loaded[publicSourceKey(item.scope)]).filter((item):item is Loaded=>!!item);
  const loading = pages.some(page=>page.loading);
  useLayoutEffect(()=>{
    const saved=anchor.current;
    if (saved && !loading) {
      if (saved.ownerKey === ownerKey && saved.element.isConnected && saved.scroller.isConnected && marker.current?.contains(saved.element))
        saved.scroller.scrollTop += saved.element.getBoundingClientRect().top - saved.top;
      anchor.current=null;
    }
  },[signature,loading,ownerKey]);
  function loadEarlier() {
    const scroller=marker.current?.closest(".chat__scroll");
    const rows=marker.current?.querySelectorAll("li");
    const top=scroller?.getBoundingClientRect().top ?? 0;
    const element=rows && ([...rows].find(row=>row.getBoundingClientRect().bottom > top) ?? rows[0]);
    if(element && scroller) anchor.current={element,scroller,ownerKey,top:element.getBoundingClientRect().top};
    pages.filter(page=>page.hasOlder).forEach(page=>page.loadOlder());
  }
  // Real tool steps exist only for native Claude turns (the Astra relay has its own timeline).
  const claudeSource = sources.find(item=>item.scope.provider === "claude");
  const hasOlder = pages.some(page=>page.hasOlder);
  const error = pages.some(page=>page.error) || discovered?.key === ownerKey && discovered.error;
  const request = messages.find(message=>message.role === "user" && message.id === requestMessageId);
  const requestCreatedAt = request?.createdAt;
  const hasFinal = messages.some(message=>message.role === "assistant" && !message.isStreaming &&
    (!!message.content?.trim() || !!message.files?.length));
  const lastScope = groups.at(-1)?.items.at(-1)?.scope;
  const terminal = (lastScope ? byRun.get(publicSourceKey(lastScope))?.terminalStatus : sources.at(-1)?.terminalStatus)
    ?? (engaged && !pending && !astra && !claude && !hasFinal ? "Updates saved" : undefined);
  // The run finished but its reply is not on screen yet: never say "saved" over an empty
  // chat — the reply is on its way (it lands within seconds; after a minute, stop claiming).
  // Only the chat's latest request: lastDoneAt is chat-wide, so an older card must not claim it.
  const latestRequest = [...(allMessages ?? messages)].reverse().find(message=>message.role === "user")?.id === requestMessageId;
  const replyDue = latestRequest && terminal === "Updates saved" && !hasFinal && typeof status?.lastDoneAt === "number"
    && Date.now() - status.lastDoneAt < 60_000;
  const shownTerminal = replyDue ? "Writing reply…" : terminal;
  const live = !!astra || !!claude || pending;
  const showFrame = live || groups.length > 0 || sources.length > 0 || engaged && !hasFinal;
  // Native input handoffs may briefly change the live recorder locator. Already
  // validated, exact-request timeline items still prove this task has started.
  const preparing = pending && !astra && !claude && !groups.some(group => group.items.length > 0);
  const requestedName = requestedProgressName(status,companyId,request?.content ?? "","operations");
  // Config titles the pending frame only; native archive models remain authoritative.
  const emptyName = astra ? "Astra" : claude?.model ? claudeUpdateName(claude)
    : live && requestedName !== "Agent" ? requestedName
    : claude ? "Claude" : sources.at(-1)?.displayName ?? "Agent";
  const frames = groups.length ? groups : showFrame ? [{key:ownerKey+":first",items:[],displayName:emptyName,
    active:live,phase:preparing?undefined:astra?.status==="waiting"?"Queued":live?"Working":shownTerminal}] : [];
  const inTranscriptSheet = useContextRPU(TranscriptInSheet);
  const openTranscriptSheet = useTranscriptSheet(state=>state.open);
  const openThisTranscript = inTranscriptSheet ? null : () => openTranscriptSheet(requestMessageId);
  return <TranscriptOpener.Provider value={openThisTranscript}><div ref={marker} className={"delegateArchiveAnchor requestPublicUpdates" + (groups.length || showFrame ? " has-updates" : "")} data-request-message-id={requestMessageId}>
    {sources.map(item=><SourceLoader key={publicSourceKey(item.scope)} source={item}
      enabled={visible || inTranscriptSheet || !!item.active} onChange={publish} />)}
    {showFrame && <>
      {hasOlder && <button type="button" className="delegateProgress__load" disabled={loading}
        onClick={loadEarlier}>Load earlier updates</button>}
      {error && <p className="delegateProgress__archiveError">Some earlier updates are unavailable.
        <button type="button" onClick={()=>{setReload(value=>value+1);pages.forEach(page=>page.retry());}}>Retry</button></p>}
      {hasOlder && <p className="delegateProgress__empty">Showing loaded updates in timestamp order.</p>}
      {frames.map((group,index)=><section className="msg msg--ai delegateSavedUpdates" key={group.key===first?ownerKey+":first":group.key}>
        <DelegateTimelineSegment segmentKey={group.key} items={group.items} displayName={group.displayName as "Astra"|"Claude"|"Opus"|"Agent"}
          active={group.active || (!live && index===frames.length-1 && replyDue)} phase={!live && index===frames.length-1 ? shownTerminal : group.phase} />
      </section>)}
      {SHOW_RUN_STEPS && claudeSource && companyId && <ClaudeRunSteps key={companyId+":"+claudeSource.scope.runId} companyId={companyId} runId={claudeSource.scope.runId}
        requestMessageId={requestMessageId} live={!!claude} />}
      {live && <RequestProgressFooter astra={astra} preparing={preparing} working={!preparing}
        claudeName={claude ? claudeUpdateName(claude) : undefined} requestCreatedAt={requestCreatedAt} />}
    </>}
  </div></TranscriptOpener.Provider>;
}
